import { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import {
  pathConverter,
  iframePaths,
  iframePathPatterns,
  shouldNavigate,
  checkIframeOrigin,
} from '../utils';
import { useIframeCacheInvalidation, useActiveIframePaths } from '../hooks';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DISABLE_IFRAME_BG_LOADING } from 'config';

type IframeProps = {
  path: string;
};

export const Iframe = ({ path }: IframeProps) => {
  const [iframePath, setIframePath] = useState('');
  const [loading, setLoading] = useState(false);
  const activePaths = useActiveIframePaths();

  const matchesPath =
    activePaths.includes(path) ||
    activePaths.some(p => path.toLowerCase().includes(p)) ||
    iframePathPatterns.some(pattern => pattern.test(path));

  const hidden = !matchesPath;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useIframeCacheInvalidation(iframePath, queryClient);

  useEffect(() => {
    if (
      (activePaths.includes(path) && iframePath !== path) ||
      activePaths.some(p => path.toLowerCase().includes(p)) ||
      iframePathPatterns.some(pattern => pattern.test(path))
    ) {
      if (!iframePath) {
        setLoading(true);
      }
      setIframePath(path);
    }
  }, [path, iframePath, activePaths]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Preload /style iframe so any iframe page opens fast; loading one page loads the whole platform.
    if (!DISABLE_IFRAME_BG_LOADING) {
      timeoutId = setTimeout(() => {
        // Use functional update so an already-set deep-link path (e.g. from an email
        // link like /style/116/communicationlog) is not overwritten with the generic
        // /style preload path, which would interrupt the legacy SPA navigation and
        // cause the style list to appear empty on first load.
        setIframePath(prev => prev || '/style');
      }, 1000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (wrapperRef.current) {
      const debouncedHandleResize = debounce(entries => {
        for (const entry of entries) {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }, 200);
      const observer = new ResizeObserver(debouncedHandleResize);
      observer.observe(wrapperRef.current);
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowed = checkIframeOrigin(event.origin);
      if (!allowed) {
        return;
      }
      if (event.data.type === 'relocateTo' && !hidden) {
        const newUrl = shouldNavigate(event.data.payload, location.pathname);
        if (newUrl) {
          navigate(newUrl);
        }
      }
    };
    window?.addEventListener('message', handleMessage);
    return () => {
      window?.removeEventListener('message', handleMessage);
    };
  }, [location, hidden]);

  return (
    <IframeWrapper ref={wrapperRef} hidden={hidden || !iframePath}>
      {iframePath ? (
        <>
          <HidableIframe>
            <IframeEl
              title={path}
              id="iframe-old-spa"
              width={dimensions.width}
              height={dimensions.height}
              src={pathConverter(iframePath)}
              onLoad={() => {
                setLoading(false);
              }}
            />
          </HidableIframe>
          {loading ? <Loader>Loading iframe..</Loader> : null}
        </>
      ) : null}
    </IframeWrapper>
  );
};

const IframeEl = styled.iframe`
  border: none;
`;

const IframeWrapper = styled.div`
  position: relative;
  display: flex;
  height: ${p => (p.hidden ? '0' : '100vh')};
  width: 100%;
`;

const HidableIframe = styled.div`
  position: absolute;
  inset: 0;
`;

const Loader = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: 200px;
  height: 50px;
  top: 30px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
`;
